import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MapPin, Anchor, Train, Phone as PhoneIcon, AlertTriangle, Search, Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from '@/context/AuthContext';
import { formatUsPhone, isValidUsPhone } from '@/lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadTypes = ['FTL', 'LTL', 'Intermodal', 'Drayage'];
const truckTypes = ['Dry Van', 'Flatbed', 'Reefer', 'Container', 'Power Only', 'Step Deck'];
const statuses = ['available', 'assigned', 'completed'];

export const LoadFormModal = ({ load, onClose, onSuccess, canManageAll = false }) => {
  const { user, updateProfile } = useAuth();
  const [regions, setRegions] = useState([]);
  const [formData, setFormData] = useState({
    load_type: load?.load_type || 'FTL',
    pickup: load?.pickup || '',
    destination: load?.destination || '',
    weight: load?.weight || '',
    truck_type: load?.truck_type || '',
    commodity: load?.commodity || '',
    pickup_date: load?.pickup_date || '',
    latest_date: load?.latest_date || '',
    delivery_date: load?.delivery_date || '',
    rate: load?.rate || '',
    miles: load?.miles || '',
    origin_region_id: load?.origin_region_id || '',
    destination_region_id: load?.destination_region_id || '',
    port_region_id: load?.port_region_id || '',
    status: load?.status || 'available',
    // Manager-only contact overrides
    agent_name: load?.agent_name || '',
    agent_email: load?.agent_email || '',
    agent_phone: load?.agent_phone || '',
  });
  const [submitting, setSubmitting] = useState(false);
  // Post-load "Your contact phone" — auto-fills from user profile, saved back on success
  const [myPhone, setMyPhone] = useState(formatUsPhone(user?.phone || ''));
  const isNew = !load;
  const hasProfilePhone = !!(user?.phone && user.phone.trim());

  useEffect(() => {
    axios.get(`${API}/regions`).then(res => setRegions(res.data)).catch(() => {});
  }, []);

  const isDrayage = formData.load_type === 'Drayage';
  const states = regions.filter(r => r.region_type === 'STATE');
  const usStates = states.filter(r => (r.country || 'US') === 'US');
  const caProvinces = states.filter(r => r.country === 'CA');
  const portsAndRails = regions.filter(r => r.region_type === 'PORT' || r.region_type === 'RAIL');

  // ==================== ZIP / Postal-code auto-lookup ====================
  // As the user types in Pickup / Destination, if the string looks like a US ZIP
  // or a Canadian postal code (or FSA), hit /api/geo/zip-lookup and pre-fill
  // "City, ST" + auto-pick the matching state/province in the region dropdown.
  const [lookupBusy, setLookupBusy] = useState({ pickup: false, destination: false });
  const lookupTimersRef = useRef({});

  const looksLikePostal = (s) => {
    const raw = String(s || '').trim().toUpperCase().replace(/[\s-]/g, '');
    if (/^\d{5}$/.test(raw)) return raw;                               // US ZIP
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(raw)) return raw.slice(0, 3);   // CA full postal → FSA
    if (/^[A-Z]\d[A-Z]$/.test(raw)) return raw;                        // CA FSA
    return null;
  };

  const findRegionIdFor = (stateCode, country) => {
    if (!stateCode) return '';
    const match = states.find(r =>
      (r.country || 'US') === country && (r.code || '').toUpperCase() === stateCode.toUpperCase()
    );
    return match?.id || '';
  };

  const runZipLookup = async (field, rawValue) => {
    const normalized = looksLikePostal(rawValue);
    if (!normalized) return;
    setLookupBusy((s) => ({ ...s, [field]: true }));
    try {
      const res = await axios.get(`${API}/geo/zip-lookup`, { params: { code: normalized } });
      const { city, state_code, country } = res.data;
      const composed = `${city}, ${state_code}`.trim();
      setFormData((prev) => {
        const next = { ...prev, [field]: composed };
        // Auto-fill region on the corresponding side (only when Drayage isn't active)
        if (prev.load_type !== 'Drayage') {
          const rid = findRegionIdFor(state_code, country);
          if (rid) {
            if (field === 'pickup') next.origin_region_id = rid;
            if (field === 'destination') next.destination_region_id = rid;
          }
        }
        return next;
      });
      toast.success(`${normalized} → ${composed}${country === 'CA' ? ' (Canada)' : ''}`);
    } catch (e) {
      const status = e.response?.status;
      if (status && status !== 400) {
        // 400 = invalid format (silent — user is still typing); 404/502 → surface it
        toast.error(e.response?.data?.detail || 'Could not look up that postal code');
      }
    } finally {
      setLookupBusy((s) => ({ ...s, [field]: false }));
    }
  };

  const handleLocationChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Debounce the auto-lookup so it fires ~500ms after the user stops typing
    if (lookupTimersRef.current[field]) clearTimeout(lookupTimersRef.current[field]);
    if (looksLikePostal(value)) {
      lookupTimersRef.current[field] = setTimeout(() => runZipLookup(field, value), 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate the "your phone" field only when it's non-empty
    if (isNew && myPhone.trim() && !isValidUsPhone(myPhone)) {
      toast.error('Enter a full 10-digit US phone number or leave it blank');
      return;
    }
    setSubmitting(true);

    try {
      // If the user provided or updated their phone and it differs from profile, persist it first.
      const trimmedMyPhone = myPhone.trim();
      if (isNew && trimmedMyPhone && trimmedMyPhone !== (user?.phone || '')) {
        try {
          await updateProfile({ phone: trimmedMyPhone });
        } catch (profileErr) {
          const detail = profileErr?.response?.data?.detail;
          toast.error(detail || 'Could not save your phone to your profile');
          setSubmitting(false);
          return;
        }
      }

      const data = {
        ...formData,
        weight: parseFloat(formData.weight),
        rate: formData.rate ? parseFloat(formData.rate) : null,
        miles: formData.miles ? parseInt(formData.miles) : null,
        origin_region_id: formData.origin_region_id || null,
        destination_region_id: formData.destination_region_id || null,
        port_region_id: formData.port_region_id || null,
      };

      // Strip manager-only fields unless this is an edit by a manager.
      if (!load || !canManageAll) {
        delete data.agent_name;
        delete data.agent_email;
        delete data.agent_phone;
      }

      if (load) {
        await axios.put(`${API}/loads/${load.id}`, data);
        toast.success('Load updated successfully');
      } else {
        await axios.post(`${API}/loads`, data);
        toast.success('Load created — matching carriers will be notified automatically');
      }
      onSuccess();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || (load ? 'Failed to update load' : 'Failed to create load'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">
            {load ? 'Edit Load' : 'Post New Load'}
          </h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Load Type *</label>
              <Select value={formData.load_type} onValueChange={(value) => setFormData({ ...formData, load_type: value, port_region_id: '', origin_region_id: '', destination_region_id: '' })}>
                <SelectTrigger className="h-12 border-zinc-300" data-testid="load-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {loadTypes.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Equipment Type *</label>
              <Select value={formData.truck_type || "select"} onValueChange={(value) => setFormData({ ...formData, truck_type: value === "select" ? "" : value })}>
                <SelectTrigger className="h-12 border-zinc-300" data-testid="truck-type-select"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="select" disabled>Select equipment</SelectItem>
                  {truckTypes.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Pickup Location * <span className="text-zinc-400 font-normal normal-case ml-1">(City, ST/Province — or ZIP/postal to auto-fill)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="pickup"
                  value={formData.pickup}
                  onChange={handleLocationChange('pickup')}
                  required
                  className="w-full px-4 py-3 pr-10 border border-zinc-300 focus:border-zinc-900 outline-none"
                  placeholder="e.g. Chicago, IL — or 60601 — or M5H 2N2"
                  data-testid="load-pickup"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" data-testid="load-pickup-hint">
                  {lookupBusy.pickup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Destination * <span className="text-zinc-400 font-normal normal-case ml-1">(City, ST/Province — or ZIP/postal to auto-fill)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleLocationChange('destination')}
                  required
                  className="w-full px-4 py-3 pr-10 border border-zinc-300 focus:border-zinc-900 outline-none"
                  placeholder="e.g. Detroit, MI — or 48226 — or L4W 5N6"
                  data-testid="load-destination"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" data-testid="load-destination-hint">
                  {lookupBusy.destination ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </span>
              </div>
            </div>
          </div>

          {/* Region Selection — contextual based on load type */}
          {isDrayage ? (
            <div className="bg-orange-50 border border-orange-200 p-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-orange-700 mb-2">
                <Anchor className="w-3.5 h-3.5 inline mr-1" /> Port / Rail Ramp
              </label>
              <p className="text-xs text-orange-600 mb-3">Select the port or rail ramp for this drayage load. Matching drayage carriers will be auto-notified.</p>
              <Select value={formData.port_region_id || 'none'} onValueChange={(v) => setFormData({ ...formData, port_region_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="h-10 border-orange-300 bg-white" data-testid="port-region-select"><SelectValue placeholder="Select port/ramp" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific port</SelectItem>
                  {portsAndRails.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.region_type === 'PORT' ? '\u2693 ' : '\ud83d\udee4\ufe0f '}{r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 p-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">
                <MapPin className="w-3.5 h-3.5 inline mr-1" /> Region Targeting (for carrier notifications)
              </label>
              <p className="text-xs text-blue-600 mb-3">Select origin/destination state or province. OTR carriers in these regions + nationwide carriers will be auto-notified.</p>
              <div className="grid grid-cols-2 gap-3">
                <Select value={formData.origin_region_id || 'none'} onValueChange={(v) => setFormData({ ...formData, origin_region_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10 border-blue-300 bg-white" data-testid="origin-region-select"><SelectValue placeholder="Origin state/province" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any origin</SelectItem>
                    {usStates.length > 0 && (
                      <div className="px-2 py-1 text-[10px] font-black uppercase tracking-wider text-zinc-400">🇺🇸 United States</div>
                    )}
                    {usStates.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</SelectItem>)}
                    {caProvinces.length > 0 && (
                      <div className="px-2 py-1 mt-1 text-[10px] font-black uppercase tracking-wider text-zinc-400 border-t border-zinc-100">🇨🇦 Canada</div>
                    )}
                    {caProvinces.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={formData.destination_region_id || 'none'} onValueChange={(v) => setFormData({ ...formData, destination_region_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10 border-blue-300 bg-white" data-testid="dest-region-select"><SelectValue placeholder="Destination state/province" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any destination</SelectItem>
                    {usStates.length > 0 && (
                      <div className="px-2 py-1 text-[10px] font-black uppercase tracking-wider text-zinc-400">🇺🇸 United States</div>
                    )}
                    {usStates.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</SelectItem>)}
                    {caProvinces.length > 0 && (
                      <div className="px-2 py-1 mt-1 text-[10px] font-black uppercase tracking-wider text-zinc-400 border-t border-zinc-100">🇨🇦 Canada</div>
                    )}
                    {caProvinces.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Pickup Date *</label>
              <input type="date" name="pickup_date" value={formData.pickup_date} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" data-testid="load-pickup-date" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Latest Pickup</label>
              <input type="date" name="latest_date" value={formData.latest_date} onChange={handleChange} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" data-testid="load-latest-date" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Delivery Date *</label>
              <input type="date" name="delivery_date" value={formData.delivery_date} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" data-testid="load-delivery-date" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Weight (lbs) *</label>
              <input type="number" name="weight" value={formData.weight} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="40000" data-testid="load-weight" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Rate ($)</label>
              <input type="number" name="rate" value={formData.rate} onChange={handleChange} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="2500" data-testid="load-rate" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Miles</label>
              <input type="number" name="miles" value={formData.miles} onChange={handleChange} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="500" data-testid="load-miles" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Commodity *</label>
            <input type="text" name="commodity" value={formData.commodity} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="e.g., Electronics, Produce, Steel" data-testid="load-commodity" />
          </div>

          {!load && (
            <div className={`border p-4 space-y-2 ${hasProfilePhone ? 'bg-zinc-50 border-zinc-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                <PhoneIcon className={`w-4 h-4 ${hasProfilePhone ? 'text-zinc-600' : 'text-amber-600'}`} />
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-zinc-700">
                  Your Contact Phone
                </label>
                {hasProfilePhone && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-2 py-0.5">
                    From your profile
                  </span>
                )}
              </div>
              <input
                type="tel"
                value={myPhone}
                onChange={(e) => setMyPhone(formatUsPhone(e.target.value))}
                placeholder="(555) 123-4567"
                className={`w-full px-3 py-2.5 text-sm font-mono border outline-none ${hasProfilePhone ? 'border-zinc-300 focus:border-zinc-900' : 'border-amber-300 focus:border-amber-600'}`}
                data-testid="load-my-phone"
              />
              {!myPhone.trim() && (
                <div className="flex gap-2 items-start text-[11px] text-amber-800" data-testid="load-my-phone-warning">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    Leave blank to use the company hotline <strong>1 (877) 680-9272</strong>.
                    Adding your direct US number helps carriers reach <em>you</em> faster.
                  </span>
                </div>
              )}
              {myPhone.trim() && (
                <p className="text-[11px] text-zinc-500">
                  This number appears on the public load board and is saved to your profile for future posts.
                </p>
              )}
            </div>
          )}

          {load && canManageAll && (
            <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-700">
                Dispatcher Contact (manager override)
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                These fields shown on the public load board. Empty values fall back to the company defaults.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Posted By (Name)</label>
                  <input
                    type="text"
                    name="agent_name"
                    value={formData.agent_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="Jane Smith"
                    data-testid="load-agent-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Dispatcher Email</label>
                  <input
                    type="email"
                    name="agent_email"
                    value={formData.agent_email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="dispatcher@redballxpress.us"
                    data-testid="load-agent-email"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Dispatcher Phone</label>
                  <input
                    type="tel"
                    name="agent_phone"
                    value={formData.agent_phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="1 (877) 680-9272"
                    data-testid="load-agent-phone"
                  />
                </div>
              </div>
            </div>
          )}

          {load && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Status</label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger className="h-12 border-zinc-300" data-testid="load-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (<SelectItem key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!load && (
            <div className="bg-green-50 border border-green-200 p-3 text-xs text-green-700">
              Matching carriers will be <strong>automatically notified</strong> by email when this load is posted.
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="flex-1 border-2 border-zinc-300 text-zinc-700 hover:border-zinc-400 py-3 font-bold uppercase tracking-wide">Cancel</button>
            <button type="submit" disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50" data-testid="save-load-btn">
              {submitting ? 'Saving...' : (load ? 'Update Load' : 'Post Load')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
